import {ConjuntoCategoriaContabil} from '../../../core/conjunto-categoria-contabil';
import {Component, OnInit} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {ConjuntoCategoriaContabilService} from 'src/app/services/conjunto-categoria-contabil/conjunto-categoria-contabil.service';
import {CategoriasContabeisService} from 'src/app/services/categorias-contabeis/categorias-contabeis.service';
import {ToastService} from 'src/app/services/toast/toast.service';
import {ConfiguracaoSeletor} from '../../shared/interfaces/opcao-selecao.interface';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {ConfirmDialogComponent} from '../../common/confirm-dialog/confirm-dialog.component';
import {Acesso} from 'src/app/core/acesso';
import {CarregarPerfil} from 'src/app/core/carregar-perfil';
import {PlanosContas} from '../../../core/planos-contas';
import {TipoLancamentoExtrato} from '../../../core/tipo-lancamento-extrato';
import {TipoLancamentoExtratoService} from 'src/app/services/tipo-lancamento-extrato/tipo-lancamento-extrato.service';

@Component({
    selector: 'cadastrar-argumento-categoria-contabil',
    templateUrl: './cadastrar-conjunto-categoria-contabil.component.html',
    styleUrls: ['./cadastrar-conjunto-categoria-contabil.component.css']
})
export class CadastrarConjuntoCategoriaContabilComponent implements OnInit {

    form: FormGroup;
    conjunto: ConjuntoCategoriaContabil = new ConjuntoCategoriaContabil();
    comboCategorias: any[] = [];
    comboTiposLancamento: TipoLancamentoExtrato[] = [];
    isEdicao: boolean = false;

    perfilAcesso: Acesso = new Acesso();
    carregarPerfil: CarregarPerfil = new CarregarPerfil();
    mostrarBotaoSalvar = true;

    // Configuração para o seletor genérico
    configuracaoSeletorCategoria: ConfiguracaoSeletor = {
        mostrarOpcaoVazia: false,
        obrigatorio: true,
        campoExibicao: 'planoConta',
        habilitarPesquisa: true,
        camposPesquisa: ['planoConta'],
        tamanhoMinimoPesquisa: 1
    };

    // Configuração do seletor de tipo de importação (exibe somente a descrição do tipo)
    configuracaoSeletorTipoLancamento: ConfiguracaoSeletor = {
        mostrarOpcaoVazia: true,
        textoOpcaoVazia: 'Nenhum',
        obrigatorio: false,
        campoExibicao: 'descricao',
        campoValor: 'id',
        habilitarPesquisa: false
    };

    constructor(
        private formBuilder: FormBuilder,
        private argumentoCategoriaContabilService: ConjuntoCategoriaContabilService,
        private categoriasContabeisService: CategoriasContabeisService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private toastService: ToastService,
        private dialog: MatDialog,
        private tipoLancamentoExtratoService: TipoLancamentoExtratoService
    ) {
    }

    ngOnInit() {
        this.inicializarFormulario();
        this.carregarCategorias();
        this.carregarTiposLancamento();

        this.carregarPerfil.carregar(this.activatedRoute.snapshot.data.perfilAcesso, this.perfilAcesso);

        this.verificarEdicao();
        if (!this.perfilAcesso.insere && !this.isEdicao) {
            this.mostrarBotaoSalvar = false;
            this.router.navigate(['/conjuntos-categoria-contabil']);
        }
        if (!this.perfilAcesso.altera && this.isEdicao) {
            this.mostrarBotaoSalvar = false;
        }
    }

    inicializarFormulario() {
        this.form = this.formBuilder.group({
            idCategoriaOrigem: ['', Validators.required],
            idCategoriaDestino: ['', Validators.required],
            argumentos: this.formBuilder.array([], this.validarPeloMenosUmArgumento), // FormArray de inputs
            idTipoLancamento: [null],
            ativo: [true]
        });
        // Adicionar uma linha vazia inicial
        this.adicionarLinhaArgumento();
    }

    validarPeloMenosUmArgumento(control: AbstractControl): ValidationErrors | null {
        const argumentosArray = control as FormArray;
        if (!argumentosArray || !argumentosArray.controls) {
            return {peloMenosUmArgumento: true};
        }

        const argumentosValidos = argumentosArray.controls
            .map(ctrl => ctrl.value?.trim())
            .filter((arg: string) => {
                if (!arg || arg.length < 5) return false;
                // Remover wildcards e verificar se sobra texto suficiente
                const textoSemWildcards = arg.replace(/\{\*}/g, '').trim();
                return textoSemWildcards.length >= 5;
            });

        return argumentosValidos.length > 0 ? null : {peloMenosUmArgumento: true};
    }

    validarTextoComWildcard(control: AbstractControl): ValidationErrors | null {
        const valor = control.value?.trim() || '';

        // Se estiver vazio, não validar aqui (será validado pelo minlength)
        if (!valor) {
            return null;
        }

        // Remover todos os wildcards {*} do texto
        const textoSemWildcards = valor.replace(/\{\*}/g, '').trim();

        // Se após remover os wildcards não sobrar pelo menos 5 caracteres, é inválido
        if (textoSemWildcards.length < 5) {
            return {apenasWildcards: true};
        }

        return null;
    }

    carregarCategorias() {
        this.categoriasContabeisService.getAllView(false).subscribe((planosContas: PlanosContas[]) => {
            this.comboCategorias = planosContas;
        });
    }

    carregarTiposLancamento() {
        this.tipoLancamentoExtratoService.getAll().subscribe((tipos: TipoLancamentoExtrato[]) => {
            this.comboTiposLancamento = tipos || [];
        });
    }

    verificarEdicao() {
        const id = this.activatedRoute.snapshot.queryParams['id'];
        if (id) {
            this.isEdicao = true;
            this.carregarArgumento(id);
        }
    }

    carregarArgumento(id: number) {
        this.argumentoCategoriaContabilService.getById(id).subscribe((argumento: ConjuntoCategoriaContabil) => {
            if (argumento) {
                this.conjunto = argumento;
                // Limpar FormArray existente
                const argumentosArray = this.form.get('argumentos') as FormArray;
                while (argumentosArray.length !== 0) {
                    argumentosArray.removeAt(0);
                }
                // Adicionar argumentos existentes
                const argumentos = argumento.argumentos || [];
                if (argumentos.length > 0) {
                    argumentos.forEach(arg => {
                        this.adicionarControlComListener(argumentosArray, arg);
                    });
                } else {
                    // Se não houver argumentos, adicionar uma linha vazia
                    this.adicionarLinhaArgumento();
                }
                this.form.patchValue({
                    idCategoriaOrigem: argumento.idCategoriaOrigem,
                    idCategoriaDestino: argumento.idCategoriaDestino,
                    idTipoLancamento: argumento.idTipoLancamento ?? null,
                    ativo: argumento.ativo
                });
            }
        });
    }

    salvar() {
        // Validar permissões
        if (this.isEdicao && !this.perfilAcesso.altera) {
            this.toastService.showAlerta('Você não tem permissão para alterar este registro.');
            return;
        }

        if (!this.isEdicao && !this.perfilAcesso.insere) {
            this.toastService.showAlerta('Você não tem permissão para cadastrar novos registros.');
            return;
        }

        // Validar se há pelo menos um argumento válido
        const argumentosArray = this.form.get('argumentos') as FormArray;
        const argumentosValidos = argumentosArray.controls
            .map(control => control.value?.trim())
            .filter((arg: string) => {
                if (!arg || arg.length < 5) return false;
                // Remover wildcards e verificar se sobra texto suficiente
                const textoSemWildcards = arg.replace(/\{\*}/g, '').trim();
                return textoSemWildcards.length >= 5;
            });

        if (argumentosValidos.length === 0) {
            this.toastService.showAlerta('É necessário adicionar pelo menos um argumento com no mínimo 5 caracteres (sem contar apenas wildcards).');
            return;
        }

        if (this.form.valid) {
            this.conjunto.idCategoriaOrigem = this.form.get('idCategoriaOrigem')?.value;
            this.conjunto.idCategoriaDestino = this.form.get('idCategoriaDestino')?.value;
            // Obter valores do FormArray e filtrar strings vazias e espaços em branco
            this.conjunto.argumentos = argumentosValidos;
            this.conjunto.ativo = this.form.get('ativo')?.value;
            this.conjunto.idTipoLancamento = this.form.get('idTipoLancamento')?.value ?? null;

            if (this.isEdicao) {
                this.argumentoCategoriaContabilService.alterar(this.conjunto).subscribe(() => {
                    this.toastService.showSucesso('Argumento alterado com sucesso!');
                    this.voltar();
                }, (error) => {
                    this.toastService.showAlerta('Erro ao alterar argumento: ' + error.error.mensagem);
                });
            } else {
                this.argumentoCategoriaContabilService.cadastrar(this.conjunto).subscribe(() => {
                    this.toastService.showSucesso('Argumento cadastrado com sucesso!');
                    this.voltar();
                }, (error) => {
                    this.toastService.showAlerta('Erro ao cadastrar argumento: ' + error.error.mensagem);
                });
            }
        } else {
            this.toastService.showAlerta('Por favor, preencha todos os campos obrigatórios.');
        }
    }

    voltar() {
        this.router.navigate(['/conjuntos-categoria-contabil']);
    }

    getArgumentosFormArray(): FormArray {
        return this.form.get('argumentos') as FormArray;
    }

    adicionarControlComListener(argumentosArray: FormArray, valor: string) {
        const control = this.formBuilder.control(valor, [
            Validators.minLength(5),
            Validators.maxLength(255),
            this.validarTextoComWildcard.bind(this)
        ]);
        argumentosArray.push(control);
        // Adicionar listener para revalidar o FormArray quando o controle mudar
        control.valueChanges.subscribe(() => {
            argumentosArray.updateValueAndValidity();
        });
    }

    adicionarLinhaArgumento() {
        const argumentosArray = this.getArgumentosFormArray();
        this.adicionarControlComListener(argumentosArray, '');
        // Revalidar o FormArray após adicionar
        argumentosArray.updateValueAndValidity();
    }

    removerLinhaArgumento(index: number) {
        this.chamaCaixaDialogo(index);
    }

    chamaCaixaDialogo(index: number) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = {
            pergunta: 'Certeza que deseja excluir este argumento?',
            textoConfirma: 'SIM',
            textoCancela: 'NÃO'
        };

        const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
        dialogRef.afterClosed().subscribe(confirma => {
            if (confirma) {
                const argumentosArray = this.getArgumentosFormArray();
                if (argumentosArray.length > 1) {
                    argumentosArray.removeAt(index);
                    this.toastService.showSucesso('Argumento removido com sucesso!');
                } else {
                    // Se for a última linha, apenas limpar o valor ao invés de remover
                    argumentosArray.at(index).setValue('');
                    this.toastService.showSucesso('Argumento removido com sucesso!');
                }
                // Revalidar o FormArray após remover
                argumentosArray.updateValueAndValidity();
            } else {
                dialogRef.close();
            }
        });
    }

    inserirWildcard(index: number, inputElement: HTMLInputElement) {
        const argumentoControl = this.getArgumentosFormArray().at(index);
        const valorAtual = argumentoControl.value || '';
        const start = inputElement.selectionStart || valorAtual.length;
        const end = inputElement.selectionEnd || valorAtual.length;
        const wildcard = '{*}';

        // Inserir o wildcard na posição do cursor
        const novoValor = valorAtual.substring(0, start) + wildcard + valorAtual.substring(end);
        argumentoControl.setValue(novoValor);

        // Reposicionar o cursor após o wildcard inserido
        setTimeout(() => {
            inputElement.focus();
            const novaPosicao = start + wildcard.length;
            inputElement.setSelectionRange(novaPosicao, novaPosicao);
        }, 0);

        // Revalidar o FormArray após inserir wildcard
        const argumentosArray = this.getArgumentosFormArray();
        argumentosArray.updateValueAndValidity();
    }

    onCategoriaOrigemChange(event: any) {
        this.form.get('idCategoriaOrigem')?.setValue(event);
    }

    onCategoriaDestinoChange(event: any) {
        this.form.get('idCategoriaDestino')?.setValue(event);
    }

    onTipoLancamentoChange(event: any) {
        this.form.get('idTipoLancamento')?.setValue(event ?? null);
    }
}
